let currentUtterance: SpeechSynthesisUtterance | null = null

export const textToSpeech = (
  text: string, 
  language: string = 'en-US',
  rate: number = 1.0,
  pitch: number = 1.0,
  volume: number = 1.0,
  retryCount: number = 0
): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check for browser support
    if (!('speechSynthesis' in window)) {
      reject(new Error('Text-to-speech not supported in this browser'))
      return
    }

    // Stop any current speech
    if (currentUtterance) {
      window.speechSynthesis.cancel()
    }

    // Wait a bit for speech synthesis to be ready
    setTimeout(() => {
      try {
        // Create new utterance
        currentUtterance = new SpeechSynthesisUtterance(text)
        
        // Configure utterance with safer values
        currentUtterance.lang = language
        currentUtterance.rate = Math.max(0.1, Math.min(2.0, rate))
        currentUtterance.pitch = Math.max(0.0, Math.min(2.0, pitch))
        currentUtterance.volume = Math.max(0.0, Math.min(1.0, volume))

        // Try to find a voice for the specified language
        const voices = window.speechSynthesis.getVoices()
        const voice = voices.find(v => v.lang.startsWith(language.split('-')[0])) || 
                     voices.find(v => v.lang.startsWith('en')) ||
                     voices[0]
        
        if (voice) {
          currentUtterance.voice = voice
        }

        // Handle events
        currentUtterance.onend = () => {
          currentUtterance = null
          resolve()
        }

        currentUtterance.onerror = (event) => {
          currentUtterance = null
          
          // Retry logic for common errors
          if (retryCount < 2 && (event.error === 'synthesis-failed' || event.error === 'network')) {
            console.warn(`TTS error: ${event.error}, retrying... (${retryCount + 1}/2)`)
            setTimeout(() => {
              textToSpeech(text, language, rate, pitch, volume, retryCount + 1)
                .then(resolve)
                .catch(reject)
            }, 500)
            return
          }
          
          // If all retries failed, resolve silently to prevent app crashes
          console.error(`Speech synthesis error: ${event.error}`)
          resolve() // Changed from reject to resolve
        }

        // Start speaking
        window.speechSynthesis.speak(currentUtterance)
        
      } catch (error) {
        currentUtterance = null
        console.error('Failed to start speech synthesis:', error)
        resolve() // Changed from reject to resolve
      }
    }, 100) // Small delay to ensure speech synthesis is ready
  })
}

// Stop current speech
export const stopSpeaking = () => {
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel()
  }
  currentUtterance = null
}

// Check if currently speaking
export const isSpeaking = (): boolean => {
  return window.speechSynthesis?.speaking || false
}

// Get available voices
export const getAvailableVoices = (): SpeechSynthesisVoice[] => {
  if (!('speechSynthesis' in window)) {
    return []
  }
  return window.speechSynthesis.getVoices()
}

// Get voices for a specific language
export const getVoicesForLanguage = (language: string): SpeechSynthesisVoice[] => {
  const voices = getAvailableVoices()
  const langCode = language.split('-')[0]
  return voices.filter(voice => voice.lang.startsWith(langCode))
}

// Wait for voices to be loaded (some browsers load them asynchronously)
export const waitForVoices = (): Promise<SpeechSynthesisVoice[]> => {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) {
      resolve([])
      return
    }

    const voices = window.speechSynthesis.getVoices()
    if (voices.length > 0) {
      resolve(voices)
      return
    }

    // Wait for voices to load
    const handleVoicesChanged = () => {
      const voices = window.speechSynthesis.getVoices()
      if (voices.length > 0) {
        window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged)
        resolve(voices)
      }
    }

    window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged)
    
    // Fallback timeout
    setTimeout(() => {
      window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged)
      resolve(window.speechSynthesis.getVoices())
    }, 3000)
  })
}

// Get preferred voice for language
export const getPreferredVoice = (language: string): SpeechSynthesisVoice | null => {
  const voices = getVoicesForLanguage(language)
  
  if (voices.length === 0) {
    return null
  }

  // Prefer local voices over remote ones
  const localVoices = voices.filter(voice => voice.localService)
  if (localVoices.length > 0) {
    return localVoices[0]
  }

  return voices[0]
}

// Speak with automatic language detection and voice selection
export const speakWithAutoVoice = async (
  text: string,
  language: string = 'en-US',
  options: {
    rate?: number
    pitch?: number
    volume?: number
  } = {}
): Promise<void> => {
  // Wait for voices to be available
  await waitForVoices()
  
  // Get preferred voice
  const voice = getPreferredVoice(language)
  
  return textToSpeech(
    text,
    voice?.lang || language,
    options.rate || 1.0,
    options.pitch || 1.0,
    options.volume || 1.0
  )
}

// Check if text-to-speech is supported
export const isTextToSpeechSupported = (): boolean => {
  return 'speechSynthesis' in window
}

// Get language-specific greeting messages
export const getGreetingMessage = (language: string): string => {
  const greetings: Record<string, string> = {
    'en-US': 'Hello! I\'m your voice payment assistant. How can I help you today?',
    'en-GB': 'Hello! I\'m your voice payment assistant. How may I assist you today?',
    'es-ES': '¡Hola! Soy tu asistente de pagos por voz. ¿Cómo puedo ayudarte hoy?',
    'es-MX': '¡Hola! Soy tu asistente de pagos por voz. ¿Cómo te puedo ayudar hoy?',
    'fr-FR': 'Bonjour! Je suis votre assistant de paiement vocal. Comment puis-je vous aider aujourd\'hui?',
    'de-DE': 'Hallo! Ich bin Ihr Sprach-Zahlungsassistent. Wie kann ich Ihnen heute helfen?',
    'it-IT': 'Ciao! Sono il tuo assistente di pagamento vocale. Come posso aiutarti oggi?',
    'pt-BR': 'Olá! Sou seu assistente de pagamento por voz. Como posso ajudá-lo hoje?',
    'pt-PT': 'Olá! Sou o seu assistente de pagamento por voz. Como o posso ajudar hoje?',
    'ru-RU': 'Привет! Я ваш голосовой помощник по платежам. Как я могу помочь вам сегодня?',
    'ja-JP': 'こんにちは！私はあなたの音声決済アシスタントです。今日はどのようにお手伝いできますか？',
    'ko-KR': '안녕하세요! 저는 음성 결제 도우미입니다. 오늘 어떻게 도와드릴까요?',
    'zh-CN': '你好！我是您的语音支付助手。今天我可以为您做些什么？',
    'zh-TW': '您好！我是您的語音支付助手。今天我可以為您做些什麼？',
    'ar-SA': 'مرحبا! أنا مساعد الدفع الصوتي الخاص بك. كيف يمكنني مساعدتك اليوم؟',
    'hi-IN': 'नमस्ते! मैं आपका वॉयस पेमेंट असिस्टेंट हूं। आज मैं आपकी कैसे मदद कर सकता हूं?',
    'th-TH': 'สวัสดี! ฉันเป็นผู้ช่วยการชำระเงินด้วยเสียงของคุณ วันนี้ฉันจะช่วยคุณอย่างไร?',
    'vi-VN': 'Xin chào! Tôi là trợ lý thanh toán bằng giọng nói của bạn. Hôm nay tôi có thể giúp gì cho bạn?'
  }

  const langCode = language.split('-')[0]
  return greetings[language] || 
         greetings[`${langCode}-${langCode.toUpperCase()}`] || 
         greetings['en-US']
}